import { definitionTerm } from './conceptLinks';

/**
 * EUR-Lex links in a Chat answer → the same text in BARK's Library
 * (docs/specs/regulation-browser.md, "Embed mode"). Chat opens the embed URL
 * in its side panel (LibraryPanel.tsx); "Open in Library" opens `full`.
 *
 *   …/TXT/?uri=CELEX:32013R0575#art_92           → /library/embed/act/32013R0575/Article%2092
 *   …/TXT/?uri=CELEX:02013R0575-20250629#art_92  → the same + ?date=2025-06-29
 *   …#art_4:~:text=%E2%80%98own%20funds%E2%80%99 → the same + &highlight=own funds
 *   no article anchor                             → /library/embed/act/32013R0575
 *   link text "Art 4(1)(36)" (libraryTarget's `at`) → …/Article%204?at=(1)(36): the point, marked
 *   a definition link (conceptTarget)             → /library/embed/concept/find?term=own funds&celex=…
 *
 * Anything else (another site, another EUR-Lex page, a non-English text, no
 * CELEX act) has no Library destination and keeps its link as it is.
 */

export interface LibraryTarget {
    /** The Library page for the side panel (`/library/embed/…`). */
    embed: string;
    /** The same page on the website, for a full tab. */
    full: string;
    /** `Article 92`, when the link names an article. */
    article?: string;
}

const ACT = /^CELEX:[03](\d{4}[RL]\d{4,5})(?:-(\d{4})(\d{2})(\d{2}))?$/;
const ARTICLE = /^#art_(\d+[a-z]*)(?::~:(.*))?$/i;

/** The passage a text fragment (`text=[prefix-,]start[,end][,-suffix]`) names:
 *  its start, decoded, without the quote marks a quoted term carries. */
export function fragmentPassage(directives: string): string | undefined {
    const text = directives.split('&').find(d => d.startsWith('text='));
    if (!text) return undefined;
    const parts = text.slice(5).split(',');
    if (parts.length > 1 && parts[0].endsWith('-')) parts.shift();
    if (parts.length > 1 && parts[parts.length - 1].startsWith('-')) parts.pop();
    let passage: string;
    try { passage = decodeURIComponent(parts[0] || ''); } catch { return undefined; }
    passage = passage.trim();
    const quoted = /^[‘'“"]([\s\S]*)[’'”"]$/.exec(passage);
    if (quoted) passage = quoted[1].trim();
    return passage || undefined;
}

/** A EUR-Lex link to an act the Library can show: the act (base CELEX), the
 *  date of a consolidated version, the article anchor and its text fragment. */
function eurLexAct(href: string) {
    let url: URL;
    try { url = new URL(href); } catch { return null; }
    if (url.protocol !== 'https:' || url.host !== 'eur-lex.europa.eu' || url.username || url.password
        || !/^\/legal-content\/EN\/TXT(?:\/HTML)?\/?$/.test(url.pathname)) return null;
    const act = ACT.exec(url.searchParams.get('uri') || '');
    if (!act) return null;
    const [, id, year, month, day] = act;
    const anchor = ARTICLE.exec(url.hash);
    return {
        celex: `3${id}`,
        date: year ? `${year}-${month}-${day}` : undefined,
        number: anchor ? anchor[1].toLowerCase() : undefined,
        fragment: anchor?.[2],
    };
}

const urls = (origin: string, path: string, params: URLSearchParams) => {
    const query = params.toString();
    const base = origin.replace(/\/+$/, '');
    return { embed: `${base}/library/embed${path}${query ? `?${query}` : ''}`, full: `${base}/library${path}${query ? `?${query}` : ''}` };
};

/** The paragraph / point a link's text cites in the linked article:
 *  "Art 4(1)(36)" on `#art_4` → "(1)(36)". Only when the text's number is the
 *  anchor's article ("Article 92(1)" on a link to Article 26 cites nothing there). */
export function citedSubPart(text: string, article?: string): string | undefined {
    if (!article) return undefined;
    const number = article.replace(/^Article /, '');
    const cited = /\b(\d+[a-z]*)((?:\(\w+\))+)/i.exec(text);
    return cited && cited[1].toLowerCase() === number && cited[2].length <= 64 ? cited[2] : undefined;
}

/** `at`: the paragraph / point the link's text cites (citedSubPart), which the
 *  Library marks and scrolls to when there is no passage to highlight. */
export function libraryTarget(href: string, origin: string, at?: string): LibraryTarget | null {
    if (!origin) return null;
    const act = eurLexAct(href);
    if (!act) return null;
    const article = act.number ? `Article ${act.number}` : undefined;
    const params = new URLSearchParams();
    if (act.date) params.set('date', act.date);
    const passage = act.fragment ? fragmentPassage(act.fragment) : undefined;
    if (passage) params.set('highlight', passage);
    if (article && at && /^(?:\(\w+\))+$/.test(at)) params.set('at', at);
    const path = `/act/${encodeURIComponent(act.celex)}${article ? `/${encodeURIComponent(article)}` : ''}`;
    return { ...urls(origin, path, params), article };
}

/** A definition link (`#art_4:~:text='own funds'`, conceptLinks.definitionTerm)
 *  → the Library's concept page for that term in that act, through its
 *  /concept/find redirect (which falls back to the article with the term
 *  highlighted). */
export function conceptTarget(href: string, origin: string): LibraryTarget | null {
    if (!origin) return null;
    const act = eurLexAct(href);
    const term = definitionTerm(href);
    if (!act || !term) return null;
    const params = new URLSearchParams({ term, celex: act.celex });
    if (act.number) params.set('article', act.number);
    if (act.date) params.set('date', act.date);
    return urls(origin, '/concept/find', params);
}
